import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { CircularProgress } from '@mui/material'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import {
  confirmAbandonedCartDeletion,
  getAbandonedCartStatus,
  optOutAbandonedCart
} from 'services/abandonedCartService'

const AbandonedCartPage = () => {
  const { token } = useParams()
  const [status, setStatus] = useState('loading')
  const [buildingName, setBuildingName] = useState('your Building Passport')
  const [filesCount, setFilesCount] = useState(null)
  const [continueUrl, setContinueUrl] = useState('/login')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    if (!token || token === 'invalid') {
      setStatus('invalid')
      return undefined
    }

    getAbandonedCartStatus(token)
      .then(response => {
        if (cancelled) return
        const data = response?.data?.data || {}
        setBuildingName(data.buildingPassportName || 'your Building Passport')
        setFilesCount(Number.isInteger(data.filesCount) ? data.filesCount : null)
        setContinueUrl(data.continueUrl || '/login')
        setStatus(data.status || 'invalid')
      })
      .catch(() => {
        if (!cancelled) setStatus('invalid')
      })

    return () => { cancelled = true }
  }, [token])

  const optOut = async () => {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await optOutAbandonedCart(token)
      setStatus('opted_out')
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'We could not update this draft. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const buildingLabel = (
    <>
      <strong>{buildingName}</strong>
      {filesCount !== null && (
        <FilesBadge>{filesCount} uploaded {filesCount === 1 ? 'file' : 'files'}</FilesBadge>
      )}
    </>
  )

  const confirmDeletion = async () => {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await confirmAbandonedCartDeletion(token)
      setStatus('deleted')
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'We could not delete this draft. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (status === 'loading') {
    return <Page><Card><Centered><CircularProgress size={30} /><p>Checking your draft…</p></Centered></Card></Page>
  }

  if (status === 'invalid') {
    return (
      <Page><Card>
        <Icon $tone="error"><ErrorOutlineRoundedIcon /></Icon>
        <Title>This link is no longer available</Title>
        <Copy>It may have expired or already been used. No changes have been made.</Copy>
        <SecondaryLink href="/">Return to Building Passport</SecondaryLink>
      </Card></Page>
    )
  }

  if (status === 'deleted') {
    return (
      <Page><Card>
        <Icon $tone="success"><CheckCircleOutlineRoundedIcon /></Icon>
        <Title>Draft deleted</Title>
        <Copy>The draft for <strong>{buildingName}</strong> and its uploaded files have been permanently deleted.</Copy>
        <SecondaryLink href="/">Return to Building Passport</SecondaryLink>
      </Card></Page>
    )
  }

  if (status === 'converted') {
    return (
      <Page><Card>
        <Icon $tone="success"><CheckCircleOutlineRoundedIcon /></Icon>
        <Title>This Building Passport is active</Title>
        <Copy><strong>{buildingName}</strong> is no longer a draft, so it cannot be removed through this link.</Copy>
        <SecondaryLink href="/login">Sign in to view it</SecondaryLink>
      </Card></Page>
    )
  }

  if (status === 'opted_out') {
    return (
      <Page><Card>
        <Icon $tone="success"><CheckCircleOutlineRoundedIcon /></Icon>
        <Eyebrow>Reminder preferences updated</Eyebrow>
        <Title>Reminder emails stopped</Title>
        <Copy>
          You have been unsubscribed from abandoned draft reminders for {buildingLabel}.
          Your draft and uploaded files are still available and have not been deleted.
        </Copy>
        <Actions>
          <PrimaryLink href={continueUrl}>Keep and view the draft</PrimaryLink>
          <DangerTextButton type="button" onClick={() => setStatus('confirm_deletion')}>
            Delete draft and uploaded files
          </DangerTextButton>
        </Actions>
      </Card></Page>
    )
  }

  if (status === 'confirm_deletion') {
    return (
      <Page><Card>
        <Icon $tone="warning"><DeleteOutlineRoundedIcon /></Icon>
        <Eyebrow>Deletion confirmation</Eyebrow>
        <Title>Permanently delete this draft?</Title>
        <Copy>
          The draft for {buildingLabel} and every file uploaded to it will be permanently deleted.
          This action cannot be undone.
        </Copy>
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
        <Actions>
          <DangerButton type="button" onClick={confirmDeletion} disabled={submitting}>
            {submitting ? 'Deleting…' : 'Yes, permanently delete'}
          </DangerButton>
          <SecondaryButton type="button" onClick={() => setStatus('opted_out')} disabled={submitting}>
            Keep the draft without reminders
          </SecondaryButton>
        </Actions>
      </Card></Page>
    )
  }

  return (
    <Page><Card>
      <Brand>Building Passport</Brand>
      <Eyebrow>Draft reminder preferences</Eyebrow>
      <Title>Do you still need this draft?</Title>
      <Copy>
        We saved your progress for {buildingLabel}. You can keep it and continue later, or stop
        reminders and choose whether to delete it.
      </Copy>
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
      <Actions>
        <PrimaryLink href={continueUrl}>Keep and continue later</PrimaryLink>
        <TextButton type="button" onClick={optOut} disabled={submitting}>
          {submitting ? 'Updating…' : 'Stop reminder emails'}
        </TextButton>
      </Actions>
    </Card></Page>
  )
}

export default AbandonedCartPage

const Page = styled.main`
  min-height: 100vh; padding: 40px 16px; box-sizing: border-box;
  display: flex; align-items: center; justify-content: center; background: #f5f6f8;
`
const Card = styled.section`
  width: 100%; max-width: 560px; padding: 36px; box-sizing: border-box;
  background: #fff; border: 1px solid #e7e8eb; border-radius: 18px;
  box-shadow: 0 12px 38px rgba(12, 18, 41, 0.08); text-align: center;
  @media (max-width: 600px) { padding: 28px 20px; }
`
const Brand = styled.div`font-size: 16px; font-weight: 800; color: #0c1229; margin-bottom: 28px;`
const Eyebrow = styled.div`
  margin-bottom: 8px; font-size: 11px; font-weight: 800; letter-spacing: .08em;
  text-transform: uppercase; color: #6b7280;
`
const Title = styled.h1`margin: 0; color: #0c1229; font-size: 28px; line-height: 1.2;`
const Copy = styled.p`margin: 16px auto 0; max-width: 460px; color: #555b68; font-size: 15px; line-height: 1.65;`
const Actions = styled.div`display: flex; flex-direction: column; align-items: center; gap: 14px; margin-top: 28px;`
const PrimaryLink = styled.a`
  display: inline-block; padding: 12px 22px; border-radius: 7px; background: #D24724;
  color: #fff; text-decoration: none; font-weight: 700;
`
const SecondaryLink = styled.a`display: inline-block; margin-top: 22px; color: #495264; font-weight: 600;`
const TextButton = styled.button`
  display: inline-block; padding: 12px 22px; border-radius: 7px; box-sizing: border-box;
  border: 1px solid #d4d7dd; background: #fff; color: #495264;
  font: inherit; font-size: 15px; font-weight: 700; cursor: pointer;
  &:hover { background: #f5f6f8; }
  &:disabled { opacity: .6; cursor: default; }
`
const DangerTextButton = styled(TextButton)`
  color: #b42318; border-color: #e8b6b0;
  &:hover { background: #fff1f0; }
`
const SecondaryButton = styled.button`
  border: 0; background: none; color: #495264; font-weight: 600; text-decoration: underline;
  cursor: pointer; font: inherit;
  &:disabled { opacity: .6; cursor: default; }
`
const DangerButton = styled.button`
  border: 0; border-radius: 7px; padding: 12px 22px; background: #b42318; color: #fff;
  font: inherit; font-weight: 700; cursor: pointer;
  &:disabled { opacity: .65; cursor: default; }
`
const FilesBadge = styled.span`
  display: inline-block; margin-left: 8px; padding: 2px 10px; border-radius: 999px;
  background: #eef0f3; color: #3c4354; font-size: 13px; font-weight: 700; white-space: nowrap;
  vertical-align: 1px;
`
const ErrorMessage = styled.p`
  margin: 18px 0 0; padding: 10px 12px; border-radius: 7px; background: #fff1f0; color: #9f1d14; font-size: 13px;
`
const Centered = styled.div`padding: 20px; color: #555b68;`
const Icon = styled.div`
  width: 52px; height: 52px; margin: 0 auto 18px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: ${({ $tone }) => $tone === 'success' ? '#eaf8ef' : $tone === 'warning' ? '#fff3e8' : '#fff1f0'};
  color: ${({ $tone }) => $tone === 'success' ? '#247a45' : $tone === 'warning' ? '#b54708' : '#b42318'};
  svg { font-size: 28px; }
`
